import React from "react";
import { TableWrapper, TableContent, StyledTable, EmptyWrap } from "./Table.style";
import TableHeader from "./TableHeader";
import TableBody from "./TableBody";
import TablePagination from "./TablePagination";
import LoadingState from "./LoadingState";

/**
 * Table – full-featured data table with header, body, pagination, loading.
 *
 * @typedef {Object} ColumnDef
 * @property {string} key
 * @property {string} [label]
 * @property {() => React.ReactNode} [headerRender] – custom header cell (overrides `label` when set)
 * @property {boolean} [sortable]
 * @property {'left'|'center'|'right'} [align] – default 'center' (header and body, unless overridden)
 * @property {'left'|'center'|'right'} [headerAlign] – th align; long-text keys default header center
 * @property {'left'|'center'|'right'} [cellAlign] – td align; long-text keys default 'left'
 * @property {boolean} [wrap] – wrap body text; default on for address, remarks, description, etc.
 * @property {string|number} [maxWidth] – max width for wrapped cells (CSS string or px number)
 * @property {string|number} [width] – th/td column width (CSS string e.g. `'12rem'`, `'20%'`, or px number).
 *   Long-text keys get a default when omitted (see `DEFAULT_LONG_TEXT_COLUMN_WIDTH`).
 * @property {(value: any, row: any) => React.ReactNode} [render]
 *
 * @typedef {Object} TableProps
 * @property {any[]} data
 * @property {ColumnDef[]} columns
 * @property {boolean} [loading]
 * @property {string} [emptyMessage]
 * @property {string|((row: any) => string)} [rowKey]
 * @property {{ currentPage: number, pageSize: number, total: number,
 *   onPageChange: (page: number) => void }} [pagination]
 * @property {{ sortKey: string, direction: 'asc'|'desc', onSort: (key: string) => void }} [sorting]
 * @property {{ selectedRowKeys: string[], onChange: (keys: string[]) => void }} [rowSelection]
 * @property {{ label: string, onClick: (row: any) => void,
 *   variant?: 'primary'|'danger' }[]} [actions]
 * @property {string} [className]
 * @property {(row: any) => void} [onRowClick]
 * @property {{ expandedRowKeys: string[], renderExpanded: (row: any) => React.ReactNode }} [expandable]
 * @property {boolean} [stickyHeader]
 * @property {boolean} [virtualized] – reserved; currently renders all rows
 * @property {string} [loadingMessage] – text when loading (default "Loading...")
 * @property {boolean} [noHorizontalScroll] – disable horizontal scroll (for 2-column tables)
 */
export default function Table(props) {
  const {
    data = [],
    columns = [],
    loading = false,
    emptyMessage = "No data",
    rowKey = "id",
    pagination,
    sorting,
    rowSelection,
    actions,
    className,
    onRowClick,
    expandable,
    stickyHeader = false,
    virtualized = false,
    loadingMessage = "Loading...",
    noHorizontalScroll = false,
  } = props;

  const showSelection = Boolean(rowSelection);
  const showActions = Boolean(actions?.length);
  const hasData = data.length > 0;

  if (loading) {
    return (
      <TableWrapper className={className} $noHorizontalScroll={noHorizontalScroll}>
        <LoadingState message={loadingMessage} />
      </TableWrapper>
    );
  }

  const colCount = columns.length + (showSelection ? 1 : 0) + (showActions ? 1 : 0);

  return (
    <TableWrapper className={className} $noHorizontalScroll={noHorizontalScroll}>
      <TableContent $noHorizontalScroll={noHorizontalScroll}>
        <StyledTable $noHorizontalScroll={noHorizontalScroll}>
          <TableHeader
            columns={columns}
            sorting={sorting}
            stickyHeader={stickyHeader}
            showSelection={showSelection}
            showActions={showActions}
            rowSelection={rowSelection}
            data={data}
            rowKey={rowKey}
          />
          {hasData ? (
            <TableBody
              data={data}
              columns={columns}
              rowKey={rowKey}
              rowSelection={rowSelection}
              actions={actions}
              onRowClick={onRowClick}
              expandable={expandable}
              striped
            />
          ) : (
            <TableBody data={[]} columns={columns} emptyMessage={emptyMessage} colCount={colCount} />
          )}
        </StyledTable>
        {pagination && hasData && (
          <TablePagination
            currentPage={pagination.currentPage}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onPageChange={pagination.onPageChange}
            pageSizeOptions={pagination.pageSizeOptions}
            onPageSizeChange={pagination.onPageSizeChange}
            hidePageButtonsWhenSinglePage={pagination.hidePageButtonsWhenSinglePage}
            noHorizontalScroll={noHorizontalScroll}
          />
        )}
      </TableContent>
    </TableWrapper>
  );
}
